/**
 * Number and currency formatting utilities
 */

import { FormatRegistry } from "./registry";
import type { CurrencyFormat, FieldValue, FormattedValue, NumberFormat } from "./types";

/**
 * Check if a value is null or undefined
 */
export const isNil = (value: unknown): value is null | undefined => {
  return value === null || value === undefined;
};

/**
 * Format as percentage
 */
export const toPercentage = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format percentage (simple wrapper for consistency with other formatters)
 *
 * @param value - Numeric value (0-100 range)
 * @param precision - Number of decimal places (default: 0)
 * @returns Formatted percentage string with % symbol
 */
export const formatPercent = (value: number | null | undefined, precision: number = 0): string => {
  const result = toPercentage(value, precision);
  return result ?? "—";
};

/**
 * Format as integer
 */
export const toInteger = (value: number | null | undefined): string | null => {
  if (isNil(value)) return null;
  return Math.round(value).toString();
};

/**
 * Format as float with specified decimals
 */
export const toFloat = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return value.toFixed(decimals);
};

/**
 * Format number with thousands separator
 */
export const toFormattedNumber = (
  value: number | null | undefined,
  decimals?: number,
  thousandsSeparator: boolean = true
): string | null => {
  if (isNil(value)) return null;

  const options: Intl.NumberFormatOptions = {
    useGrouping: thousandsSeparator,
  };

  if (decimals !== undefined) {
    options.minimumFractionDigits = decimals;
    options.maximumFractionDigits = decimals;
  }

  return new Intl.NumberFormat("en-US", options).format(value);
};

/**
 * Format number with compact notation (1K, 1M, etc.)
 */
export const toCompactNumber = (
  value: number | null | undefined,
  display: "short" | "long" = "short"
): string | null => {
  if (isNil(value)) return null;

  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: display,
  }).format(value);
};

/**
 * Format number with scientific notation
 */
export const toScientificNotation = (value: number | null | undefined, decimals: number = 2): string | null => {
  if (isNil(value)) return null;
  return value.toExponential(decimals);
};

/**
 * Format as currency
 */
export const toCurrency = (
  value: number | null | undefined,
  currency: string = "USD",
  options?: Partial<CurrencyFormat>
): string | null => {
  if (isNil(value)) return null;

  const formatOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
    currencyDisplay: options?.currencyDisplay ?? "symbol",
    useGrouping: options?.thousandsSeparator ?? true,
  };

  if (options?.minimumFractionDigits !== undefined) {
    formatOptions.minimumFractionDigits = options.minimumFractionDigits;
  }

  if (options?.maximumFractionDigits !== undefined) {
    formatOptions.maximumFractionDigits = options.maximumFractionDigits;
  }

  return new Intl.NumberFormat("en-US", formatOptions).format(value);
};

/**
 * Format number according to NumberFormat specification
 */
export const formatNumber = (value: FieldValue, format: NumberFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const options: Intl.NumberFormatOptions = {
    useGrouping: format.thousandsSeparator ?? false,
  };

  if (format.decimals !== undefined) {
    options.minimumFractionDigits = format.decimals;
    options.maximumFractionDigits = format.decimals;
  }

  if (format.notation) {
    options.notation = format.notation;
    if (format.notation === "compact" && format.compactDisplay) {
      options.compactDisplay = format.compactDisplay;
    }
  }

  if (format.sign) {
    options.signDisplay = format.sign;
  }

  return new Intl.NumberFormat("en-US", options).format(numValue);
};

/**
 * Format currency according to CurrencyFormat specification
 */
export const formatCurrency = (value: FieldValue, format: CurrencyFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  return toCurrency(numValue, format.currency, format);
};

// Register number formatters
FormatRegistry.register("percentage", (value, decimals) => {
  if (typeof value === "number") {
    return toPercentage(value, typeof decimals === "number" ? decimals : 0);
  }
  return null;
});

FormatRegistry.register("integer", (value) => {
  if (typeof value === "number") {
    return toInteger(value);
  }
  return null;
});

FormatRegistry.register("float", (value, decimals) => {
  if (typeof value === "number") {
    return toFloat(value, typeof decimals === "number" ? decimals : 1);
  }
  return null;
});

FormatRegistry.register("number", (value, decimals, thousandsSeparator) => {
  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (!isNaN(numValue)) {
    return toFormattedNumber(
      numValue,
      typeof decimals === "number" ? decimals : undefined,
      typeof thousandsSeparator === "boolean" ? thousandsSeparator : true
    );
  }
  return null;
});

FormatRegistry.register("compact", (value, display) => {
  if (typeof value === "number") {
    return toCompactNumber(value, display === "long" ? "long" : "short");
  }
  return null;
});

FormatRegistry.register("scientific", (value, decimals) => {
  if (typeof value === "number") {
    return toScientificNotation(value, typeof decimals === "number" ? decimals : 2);
  }
  return null;
});

FormatRegistry.register("currency", (value, currency, options) => {
  if (typeof value === "number") {
    return toCurrency(
      value,
      typeof currency === "string" ? currency : "USD",
      typeof options === "object" && options !== null ? options : {}
    );
  }
  return null;
});
